import { useEffect, useState } from 'react'
import { Eye, X, Info, AlertTriangle } from 'lucide-react'
import { apiClient } from '@/services/api/client'
import type { ConfigResponse } from '@/services/api/config'
import type { HrSettingsData } from '@/types/hr'
import { HRSettingsView } from '@/components/hr-settings/HRSettingsView'

/**
 * hr2-v2 (E8): Vorschau der HR-Ansicht für Superadmins — gespeicherter
 * Zustand, read-only. Adapter aus GET /config/hr (Werte + Feld-Metadaten +
 * Kategorien + Delegation); derselbe HrSettingsData-Typ wie /hr/settings
 * sorgt dafür, dass die Vorschau nicht von der Realität driften kann.
 */
function toHrSettingsData(res: ConfigResponse): HrSettingsData {
  const visibleKeys = new Set(
    Object.entries(res.delegation?.permissions ?? {})
      .filter(([, p]) => p.visible)
      .map(([k]) => k),
  )
  const editable = res.delegation?.permissions ?? {}
  const fields = res.fields
    .filter(f => visibleKeys.has(f.key) && f.type !== 'scope')
    .map(f => ({
      key: f.key,
      label: f.label,
      description: f.description,
      type: f.type as HrSettingsData['fields'][number]['type'],
      default: f.default,
      min_value: f.min_value,
      max_value: f.max_value,
      choices: f.choices,
      choice_labels: f.choice_labels,
      delegatable: f.delegatable,
      editable: editable[f.key]?.editable ?? false,
    }))
  const categories = res.categories
    .filter(sg => sg.name !== 'Modul')
    .map(sg => ({ ...sg, keys: sg.keys.filter(k => visibleKeys.has(k) && k !== 'hr_module_enabled') }))
    .filter(sg => sg.keys.length > 0)
  const values: Record<string, unknown> = {}
  for (const key of visibleKeys) values[key] = res.config[key]
  return { values, fields, categories }
}

export function HRPreviewModal({ onClose }: { onClose: () => void }) {
  const [data, setData] = useState<HrSettingsData | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    apiClient.get('/api/v1/config/hr')
      .then(res => {
        if (alive) setData(toHrSettingsData(res.data as ConfigResponse))
      })
      .catch(() => { if (alive) setError('Vorschau konnte nicht geladen werden') })
    return () => { alive = false }
  }, [])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b bg-purple-50">
          <div className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-purple-600" />
            <h2 className="text-lg font-semibold text-purple-900">Vorschau — HR-Ansicht</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-purple-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-2 bg-yellow-50 border-b border-yellow-200">
          <p className="text-sm text-yellow-800 flex items-center gap-2">
            <Info className="w-4 h-4" />
            Gespeicherter Zustand — in der Vorschau ist nichts bearbeitbar.
          </p>
        </div>

        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          {error && (
            <div className="p-8 text-center text-red-600">
              <AlertTriangle className="w-8 h-8 mx-auto mb-2" />
              <p>{error}</p>
            </div>
          )}
          {!error && !data && (
            <div className="flex items-center justify-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
            </div>
          )}
          {!error && data && (
            <HRSettingsView data={data} editable={false} overrides={{}} />
          )}
        </div>
      </div>
    </div>
  )
}
